import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useSite } from "@/components/site/site-data";
import { PageHeader } from "@/components/site/PageHeader";
import { submitRegistration } from "@/lib/api";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact & Înregistrare — InvesTenergy 2026" },
      {
        name: "description",
        content:
          "Înregistrare la Conferința anuală InvesTenergy 2026 și la Gala Specialiștilor din Energie. Accesul se face pe bază de confirmare.",
      },
      { property: "og:title", content: "Contact & Înregistrare — InvesTenergy 2026" },
      {
        property: "og:description",
        content: "Completați formularul de înregistrare pentru evenimentul din 6 octombrie 2026.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Contact,
});

function Contact() {
  const { blocks, settings } = useSite();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const open = settings["registrations_open"] !== "false";

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setLoading(true);
    try {
      const res = await submitRegistration({
          full_name: String(fd.get("full_name") ?? ""),
          email: String(fd.get("email") ?? ""),
          phone: String(fd.get("phone") ?? ""),
          company: String(fd.get("company") ?? ""),
          job_title: String(fd.get("job_title") ?? ""),
          message: String(fd.get("message") ?? ""),
      });
      if (res.ok) {
        setDone(true);
        toast.success("Înregistrarea a fost trimisă. Vă vom contacta pentru confirmare.");
      } else {
        toast.error(settings["registrations_closed_message"] ?? "Înregistrările sunt închise.");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "A apărut o eroare.");
    } finally {
      setLoading(false);
    }
  }

  const input =
    "mt-1 w-full rounded-md border bg-background px-4 py-3 text-sm outline-none focus:border-primary";

  return (
    <main>
      <PageHeader title="Contact & Înregistrare" subtitle="6 octombrie 2026 · Le Château, București" />
      <section className="mx-auto grid max-w-6xl gap-14 px-6 py-16 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <p className="text-lg leading-relaxed whitespace-pre-line text-foreground/90">
            {blocks["contact_intro"]?.body}
          </p>

          {!open ? (
            <div className="mt-10 rounded-xl border border-dashed p-8">
              <p className="font-display text-lg font-bold">Înregistrări închise</p>
              <p className="mt-2 text-muted-foreground">
                {settings["registrations_closed_message"]}
              </p>
            </div>
          ) : done ? (
            <div className="mt-10 rounded-xl border bg-secondary/60 p-8">
              <p className="font-display text-lg font-bold">Vă mulțumim!</p>
              <p className="mt-2 text-muted-foreground">
                Am primit înregistrarea dumneavoastră. Accesul se face pe bază de confirmare.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-10 grid gap-5 sm:grid-cols-2">
              <label className="text-sm font-medium">
                Nume și prenume *
                <input name="full_name" required className={input} />
              </label>
              <label className="text-sm font-medium">
                Email *
                <input name="email" type="email" required className={input} />
              </label>
              <label className="text-sm font-medium">
                Telefon
                <input name="phone" className={input} />
              </label>
              <label className="text-sm font-medium">
                Companie / instituție
                <input name="company" className={input} />
              </label>
              <label className="text-sm font-medium sm:col-span-2">
                Funcție
                <input name="job_title" className={input} />
              </label>
              <label className="text-sm font-medium sm:col-span-2">
                Mesaj
                <textarea name="message" rows={4} className={input} />
              </label>
              <button
                type="submit"
                disabled={loading}
                className="rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-60 sm:col-span-2 sm:justify-self-start"
              >
                {loading ? "Se trimite…" : "Trimite înregistrarea"}
              </button>
            </form>
          )}
        </div>

        <aside className="h-fit rounded-xl border bg-card p-8 shadow-lift">
          <h2 className="font-display text-xl font-bold">Date de contact</h2>
          <dl className="mt-6 space-y-4 text-sm">
            <div>
              <dt className="text-muted-foreground">Adresă</dt>
              <dd className="font-medium">{settings["event_address"]}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Email</dt>
              <dd className="font-medium">
                <a href={`mailto:${settings["contact_email"]}`} className="hover:text-primary">
                  {settings["contact_email"]}
                </a>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Telefon</dt>
              <dd className="font-medium">{settings["contact_phone"]}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Locație eveniment</dt>
              <dd className="font-medium">{settings["event_location"]}</dd>
            </div>
          </dl>
        </aside>
      </section>

    </main>
  );
}
